/**
 * CondicionActualForm — Formulario de condición actual del paciente en la visita.
 *
 * Requirements: M4.3.3
 * Issue #98: Selector de fecha/hora de última ingesta.
 *   - hora_ultima_comida se guarda como ISO 8601 (DateTimeField en backend)
 *   - horas_ayuno se calcula automáticamente en el backend al guardar
 *   - Se muestra preview de horas de ayuno en tiempo real al seleccionar fecha
 */

import { useTranslation } from "react-i18next";
import { Clock } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { DateTimePicker } from "@/components/ui/datetime-picker";
import type { CondicionActualData } from "@/hooks/use-muestras";

interface CondicionActualFormProps {
  value: CondicionActualData;
  onChange: (value: CondicionActualData) => void;
}

/** Calcula horas de ayuno desde un ISO string hasta ahora (entero, igual que el backend). */
function calcularHorasPreview(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const ultima = new Date(iso);
  if (isNaN(ultima.getTime())) return null;
  const diffMs = Date.now() - ultima.getTime();
  if (diffMs < 0) return null;
  return Math.floor(diffMs / 3_600_000);
}

export function CondicionActualForm({ value, onChange }: CondicionActualFormProps) {
  const { t } = useTranslation();

  const horasPreview = calcularHorasPreview(value.hora_ultima_comida);

  return (
    <div className="space-y-4">
      {/* Ayuno */}
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <Checkbox
            id="esta_en_ayunas"
            checked={value.esta_en_ayunas ?? false}
            onCheckedChange={(checked) =>
              onChange({
                ...value,
                esta_en_ayunas: checked === true,
                ...(checked !== true && { hora_ultima_comida: null }),
              })
            }
          />
          <Label htmlFor="esta_en_ayunas" className="cursor-pointer">
            {t("muestras.encuesta.esta_en_ayunas")}
          </Label>
        </div>

        {value.esta_en_ayunas && (
          <div className="ml-6 space-y-2">
            <div className="space-y-1">
              <Label className="text-sm">{t("muestras.encuesta.hora_ultima_comida")}</Label>
              <DateTimePicker
                value={value.hora_ultima_comida ?? null}
                onChange={(iso) => onChange({ ...value, hora_ultima_comida: iso })}
                toDate={new Date()}
                className="max-w-xs"
              />
            </div>
            {horasPreview !== null && (
              <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                <Clock className="h-3.5 w-3.5" />
                <span>
                  {t("muestras.encuesta.horas_ayuno_calculadas", { horas: horasPreview })}
                </span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Síntomas */}
      <div className="space-y-1">
        <Label htmlFor="sintomas">
          {t("muestras.encuesta.sintomas_actuales")}
          <span className="text-destructive ml-1">*</span>
        </Label>
        <Textarea
          id="sintomas"
          rows={2}
          placeholder={t("muestras.encuesta.sintomas_placeholder")}
          value={value.sintomas_actuales ?? ""}
          onChange={(e) => onChange({ ...value, sintomas_actuales: e.target.value })}
          className={
            (value.sintomas_actuales ?? "").trim().length === 0 ? "border-destructive/50" : ""
          }
        />
        {(value.sintomas_actuales ?? "").trim().length === 0 && (
          <p className="text-xs text-destructive">{t("muestras.encuesta.sintomas_requerido")}</p>
        )}
      </div>

      {/* Medicamentos */}
      <div className="space-y-1">
        <Label htmlFor="medicamentos">{t("muestras.encuesta.medicamentos_recientes")}</Label>
        <Textarea
          id="medicamentos"
          rows={2}
          placeholder={t("muestras.encuesta.medicamentos_placeholder")}
          value={value.medicamentos_recientes ?? ""}
          onChange={(e) => onChange({ ...value, medicamentos_recientes: e.target.value })}
        />
      </div>

      {/* Motivo */}
      <div className="space-y-1">
        <Label htmlFor="motivo">{t("muestras.encuesta.motivo_examen")}</Label>
        <Input
          id="motivo"
          placeholder={t("muestras.encuesta.motivo_placeholder")}
          value={value.motivo_examen ?? ""}
          onChange={(e) => onChange({ ...value, motivo_examen: e.target.value })}
        />
      </div>
    </div>
  );
}
